import React, {
  useState, useEffect, useMemo, useCallback
} from 'react';
import proptypes from 'prop-types';
const { string, number, func, bool } = proptypes;
import { Stepper } from '@one-thd/sui-atomic-components';
import { debounce } from '../productListHelper';

export const MIN_QUANTITY = 1;
export const MAX_QUANTITY = 9999;

const isNumeric = (value) => /^-?\d+$/.test(value);

export const QuantityStepper = ({
  itemId,
  quantity: quantityProp,
  onQuantityChange,
  price,
  isReadOnly
}) => {
  const [quantity, setQuantity] = useState(quantityProp);

  const updateListQuantity = useMemo(() => {
    return debounce(onQuantityChange, 1000);
  }, [onQuantityChange]);

  useEffect(() => {
    if (quantity !== quantityProp) {
      setQuantity(Math.min(quantityProp, MAX_QUANTITY));
    }
  }, [quantityProp]);

  const handleQuantityUpdate = useCallback((newQuantity) => {
    let qty = newQuantity?.quantity ?? newQuantity;

    // Prevent '0' - convert to minimum
    if (qty === '0' || qty === 0) {
      qty = MIN_QUANTITY;
    }

    if (isNumeric(qty) && qty !== quantity) {
      setQuantity(qty);
      updateListQuantity({
        itemId,
        quantity: quantityProp,
        newQuantity: { quantity: qty },
        price
      });
    }
  }, [itemId, quantity, quantityProp, price, updateListQuantity]);

  const handleQuantityBlur = useCallback(() => {
    // Validate on blur: if empty or below minimum, reset to minimum
    if (quantity === '' || quantity < MIN_QUANTITY) {
      setQuantity(MIN_QUANTITY);
      updateListQuantity({
        itemId,
        quantity: quantityProp,
        newQuantity: { quantity: MIN_QUANTITY },
        price
      });
    }
  }, [itemId, quantity, quantityProp, price, updateListQuantity]);

  if (isReadOnly) {
    return <span className="sui-text-sm">{`Qty: ${quantity}`}</span>;
  }

  return (
    <div className="sui-justify-center" data-testid="quantity-picker">
      <Stepper
        onChange={handleQuantityUpdate}
        onBlur={handleQuantityBlur}
        value={quantity}
        min={MIN_QUANTITY}
        max={MAX_QUANTITY}
        fullWidth
      />
    </div>
  );
};

QuantityStepper.displayName = 'QuantityStepper';

QuantityStepper.propTypes = {
  itemId: string,
  quantity: number,
  onQuantityChange: func,
  price: number,
  isReadOnly: bool
};

QuantityStepper.defaultProps = {
  itemId: null,
  quantity: 1,
  onQuantityChange: () => {},
  price: undefined,
  isReadOnly: false
};
